import React, { useState, useEffect } from 'react';
import { chatAPI, todosAPI } from '../../services/api';
import LoadingScreen from '../LoadingScreen';
import styles from '../../styles/OnboardingScreen.module.css';

const MISSION_LOADING_PHRASE = { text: 'Creating your personalised action plan...', image: 'Creating Your Personalized Path.png' };

/**
 * Poll backend until action plan message (TodoPlan) is generated
 */
async function waitForActionPlan() {
  const maxAttempts = 120; // Max 2 minutes
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const history = await chatAPI.getHistory();

      if (history.conversationId && history.messages && history.messages.length > 0) {
        // Check if TodoPlan message exists
        const hasPlanMessage = history.messages.some(msg =>
          msg.role === 'assistant' &&
          msg.content &&
          msg.content.includes('<TodoPlan:')
        );

        if (hasPlanMessage) {
          console.log('[PlanApproval] ✅ Action plan ready');
          return { success: true, history };
        }
      }

      if (attempts % 10 === 0) {
        console.log('[PlanApproval] Waiting for plan... (attempt', attempts + 1, '/', maxAttempts, ')');
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    } catch (error) {
      console.error('[PlanApproval] Error checking for plan:', error);
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }
  }

  console.error('[PlanApproval] ❌ Plan generation timed out');
  return { success: false, error: 'timeout' };
}

/**
 * Parse TodoPlan from message content
 */
function parseTodoPlan(content) {
  const match = content.match(/<TodoPlan:(\{.*?\})>/s);
  if (!match) return null;

  try {
    return JSON.parse(match[1]);
  } catch (error) {
    console.error('[PlanApproval] Failed to parse plan:', error);
    return null;
  }
}

function PlanApprovalScreen({ onNext, onBack, user }) {
  const [isLoading, setIsLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [objective, setObjective] = useState('');
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Time-based progress animation (30 seconds to reach 95%, then hold)
  useEffect(() => {
    if (!isLoading) {
      setLoadingProgress(100);
      return;
    }

    const startTime = Date.now();
    const duration = 30000; // 30 seconds
    const maxProgress = 95; // Stop at 95%, wait for actual completion

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * maxProgress, maxProgress);
      setLoadingProgress(progress);
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlan = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await waitForActionPlan();

      if (result.success) {
        // Find the TodoPlan message
        const planMessage = result.history.messages.find(msg =>
          msg.role === 'assistant' &&
          msg.content &&
          msg.content.includes('<TodoPlan:')
        );

        if (planMessage) {
          const parsedPlan = parseTodoPlan(planMessage.content);
          if (parsedPlan) {
            setPlan(parsedPlan.tiers);
            setObjective(parsedPlan.objective || user.currentMission);
            setConversationId(result.history.conversationId);
          } else {
            setError('Failed to parse plan');
          }
        } else {
          setError('Plan not found');
        }
      } else {
        // Plan generation timed out - check if harmful content was detected
        console.log('[PlanApproval] Plan generation timed out, checking for harmful content...');

        try {
          // Fetch updated user data to see if mission was cleared
          const response = await fetch('/api/auth/profile', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const userData = await response.json();

            // If currentMission is null, it means backend detected harmful content and cleared it
            if (!userData.currentMission) {
              console.log('[PlanApproval] ⚠️ Harmful content detected - mission was cleared by backend');
              setError('harmful_content');
              return;
            }
          }
        } catch (checkError) {
          console.error('[PlanApproval] Error checking user mission:', checkError);
        }

        setError('Plan generation timed out');
      }
    } catch (err) {
      console.error('[PlanApproval] Error loading plan:', err);
      setError('Failed to load plan');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = () => {
    console.log('[PlanApproval] 🚀 Starting plan approval (async)');

    // Start approval in background
    const approvalPromise = todosAPI.approvePlan(conversationId, plan, objective, false)
      .then(response => {
        console.log('[PlanApproval] ✅ Plan approved, todos created');
        console.log('[PlanApproval] New conversation ID:', response.newConversationId);
        return response;
      })
      .catch(err => {
        console.error('[PlanApproval] ❌ Error approving plan:', err);
        throw err;
      });

    // Continue immediately to next step with the promise
    onNext(approvalPromise);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editMessage.trim()) return;

    setShowEditModal(false);
    setIsLoading(true);

    try {
      // Get conversation history for context
      const history = await chatAPI.getHistory();

      // Build conversation context with the user's edit request
      const conversationContext = [
        ...history.messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        {
          role: 'user',
          content: editMessage.trim()
        }
      ];

      console.log('[PlanApproval] Regenerating plan with edit request:', editMessage.trim());
      console.log('[PlanApproval] Existing plan:', plan);
      console.log('[PlanApproval] Conversation context length:', conversationContext.length);

      // Call propose API with existing plan and conversation context
      // This will use TODO_EDIT_PROMPT to modify the plan
      const newPlan = await todosAPI.proposePlan(
        objective,
        null, // templateId
        conversationContext,
        plan
      );

      console.log('[PlanApproval] ✅ Received updated plan with', newPlan.length, 'tiers');

      setPlan(newPlan);
      setEditMessage('');
    } catch (err) {
      console.error('[PlanApproval] Error editing plan:', err);
      setError('Failed to update plan. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.planApprovalLoading}>
        <LoadingScreen
          text={MISSION_LOADING_PHRASE.text}
          variant="progress"
          progress={loadingProgress}
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    // Special handling for harmful content
    if (error === 'harmful_content') {
      return (
        <div className={styles.planApprovalContainer}>
          <div className={styles.planApprovalError}>
            <h2>Let's Choose a Different Goal</h2>
            <p>
              I can't create an action plan for that particular goal. Let's pick something that will help you build your financial confidence and independence.
            </p>
            <div className={styles.buttonGroup}>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={onBack}
              >
                Choose a New Goal
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Regular error handling
    return (
      <div className={styles.planApprovalContainer}>
        <div className={styles.planApprovalError}>
          <h2>Unable to Generate Plan</h2>
          <p>{error}</p>
          <div className={styles.buttonGroup}>
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={loadPlan}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showEditModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalTitle}>What would you like to change?</div>
            <div className={styles.modalMessage}>
              Tell me what you'd like to adjust about this plan
            </div>
            <textarea
              className={styles.editTextarea}
              value={editMessage}
              onChange={(e) => setEditMessage(e.target.value)}
              placeholder="E.g., 'Make the first stage easier' or 'Add more steps about budgeting'"
              autoFocus
              rows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEditSubmit();
                }
              }}
            />
            <div className={styles.modalButtons}>
              <button
                className={styles.modalBtnNo}
                onClick={() => {
                  setShowEditModal(false);
                  setEditMessage('');
                }}
              >
                Cancel
              </button>
              <button
                className={styles.modalBtnYes}
                onClick={handleEditSubmit}
                disabled={!editMessage.trim()}
              >
                Send Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.planApprovalContainer}>
        <div className={styles.planApprovalHeader}>
          <h1 className={styles.title}>Your Action Plan</h1>
          <p className={styles.planObjective}>{objective}</p>
        </div>

        <div className={styles.planPreview}>
          {plan && plan.map((tier, index) => (
            <div key={index} className={styles.planTier}>
              <div className={styles.planTierHeader}>
                <span className={styles.planTierNumber}>Stage {tier.tier_number || index + 1}</span>
                <span className={styles.planTierTitle}>{tier.title}</span>
              </div>
              <ul className={styles.planTodoList}>
                {tier.todos.map((todo, todoIndex) => (
                  <li key={todoIndex} className={styles.planTodoItem}>
                    {todo.replace(/^\*\s*/, '')}
                    {todo.startsWith('*') && (
                      <span className={styles.planTodoOptional}> (optional)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className={styles.planApprovalActions}>
          <button
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={handleEdit}
          >
            Edit Plan
          </button>
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleAccept}
          >
            Accept Plan
          </button>
        </div>
      </div>
    </>
  );
}

export default PlanApprovalScreen;
